import React, { useMemo, useState } from "react";
import Button from "../Button";
import Text from "../Text";
import { COLOR_SYSTEM } from "../../design/design-system";
import {
  InputField,
  InputWrapper,
  Label,
  TextAreaField,
  Wrapper,
} from "components/atoms/Input/style";
import { Flex, Static } from "../Layout";
import { LogLevel, log } from "utils/LogUtil";

export interface InputProps {
  multiLine?: boolean;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string | React.ReactNode;
  leftItems?: React.ReactNode[];
  rightItems?: React.ReactNode[];
  unit?: string;
  value?: string;
  name?: string;
  id?: string;
  label?: string | React.ReactNode;
  subLabel?: string | React.ReactNode;
  required?: boolean;
  error?: boolean;
  hint?: string;
  hintColor?: string;
  readonly?: boolean;
  disabled?: boolean;
  disabledColor?: boolean;
  className?: string;
  style?: React.CSSProperties;
  backgroundColor?: string;
  maxLength?: number;
  showMaxLength?: boolean;
  autoComplete?: string;
  inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
  list?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClear?: (params?: any) => any;
  onClick?: (params?: any) => any;
  onFocus?: (params?: any) => any;
  onBlur?: (params?: any) => any;
  onEnter?: (e: any) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
}

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      type = "text",
      placeholder = "",
      leftItems,
      rightItems,
      unit,
      value,
      name,
      id,
      label,
      subLabel,
      required,
      disabled,
      disabledColor,
      error = false,
      hint,
      hintColor,
      multiLine = false,
      readonly = false,
      className,
      style,
      backgroundColor,
      maxLength,
      showMaxLength,
      autoComplete,
      inputMode,
      list,
      onChange = () => {},
      onClear,
      onClick,
      onFocus = () => {},
      onBlur = () => {},
      onEnter,
      onKeyDown,
      onPaste,
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false);
    const labelColor = useMemo(() => {
      if (error) {
        return COLOR_SYSTEM.get("Red")?.[300];
      } else {
        return focus ? COLOR_SYSTEM.get("Blue")?.[400] : COLOR_SYSTEM.get("Gray")?.[600];
      }
    }, [focus, error]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // IME 조합 중 키 입력(한글 조합 확정 엔터 등)은 무시
      if (e.nativeEvent?.isComposing || e.keyCode === 229) {
        return;
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
      if (!e.defaultPrevented && onEnter && e.keyCode === 13) {
        onEnter(e);
      }
    };

    // 포커스 이벤트 메모이제이션
    const handleFocus = useMemo(() => {
      return () => {
        setFocus(true);
        onFocus();
      };
    }, [onFocus]);

    // 블러 이벤트 메모이제이션
    const handleBlur = useMemo(() => {
      return () => {
        setFocus(false);
        onBlur();
      };
    }, [onBlur]);

    // 클리어 버튼 이벤트 메모이제이션
    const handleClear = useMemo(() => {
      return (e) => {
        e.stopPropagation();
        onClear();
      };
    }, [onClear]);

    return (
      <Wrapper className={className} style={style} backgroundColor={backgroundColor}>
        {label && (
          <Label>
            <div className="label__main">
              <Text textType="InputMedium" color={labelColor}>
                {label}
              </Text>
              {required && (
                <Text textType="InputSmall" color={COLOR_SYSTEM.get("Red")?.[300]}>
                  *
                </Text>
              )}
            </div>
            <div className="label__sub">
              <Text
                textType="InputSmall"
                color={
                  error
                    ? COLOR_SYSTEM.get("Red")?.[300]
                    : focus
                    ? COLOR_SYSTEM.get("Blue")?.[400]
                    : COLOR_SYSTEM.get("Gray")?.[400]
                }
              >
                {subLabel}
              </Text>
            </div>
          </Label>
        )}
        {multiLine ? (
          <div style={{ position: "relative", height: "100%" }}>
            <TextAreaField
              autoComplete="off"
              value={value}
              name={name}
              id={id}
              error={error}
              ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
              onChange={onChange}
              onClick={onClick}
              onFocus={() => {
                setFocus(true);
                onFocus();
              }}
              onBlur={() => {
                setFocus(false);
                onBlur();
              }}
            ></TextAreaField>
            {placeholder && !value && (
              <div
                className="textarea__placeholder"
                onClick={() => {
                  setFocus(true);
                  onFocus();
                }}
              >
                {placeholder}
              </div>
            )}
          </div>
        ) : (
          <InputWrapper error={error} onClick={onClick} backgroundColor={backgroundColor}>
            {leftItems && (
              <div className="left-items">
                {leftItems.map((item, index) => (
                  <React.Fragment key={index}>{item}</React.Fragment>
                ))}
              </div>
            )}
            {
              <InputField
                ref={ref as React.ForwardedRef<HTMLInputElement>}
                type={type}
                autoComplete={autoComplete || "off"}
                spellCheck="false"
                placeholder={typeof placeholder === "string" && placeholder}
                value={value}
                name={name}
                id={id}
                error={error}
                onChange={onChange}
                disabled={disabled}
                disabledColor={disabledColor}
                maxLength={maxLength ?? undefined}
                inputMode={inputMode}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onPaste={onPaste}
                readOnly={readonly}
                list={list}
              />
            }

            {focus && value && !readonly && !disabled && onClear && (
              <Button
                color="Quaternary"
                icon="X"
                size="Medium"
                type="Icon"
                variant="Ghost"
                onClick={handleClear}
              />
            )}
            {rightItems && (
              <div className="right-items">
                {rightItems.map((item, index) => (
                  <React.Fragment key={index}>{item}</React.Fragment>
                ))}
              </div>
            )}
            {unit && (
              <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[800]} className="unit">
                {unit}
              </Text>
            )}
          </InputWrapper>
        )}
        {(hint || showMaxLength) && (
          <Flex justifyContent="space-between">
            {hint ? (
              <Text
                textType="InputSmall"
                color={hintColor || COLOR_SYSTEM.get(error ? "Red" : "Gray")?.[400]}
                className="hint"
              >
                {hint}
              </Text>
            ) : (
              <Static />
            )}
            {showMaxLength && maxLength > 0 && (
              <Static>
                <Text
                  textType="InputSmall"
                  color={COLOR_SYSTEM.get("Gray")?.[500]}
                  className="unit"
                >
                  {value.length} / {maxLength}
                </Text>
              </Static>
            )}
          </Flex>
        )}
      </Wrapper>
    );
  }
);

export default Input;
